import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { Mail, Sparkles } from "lucide-react";
import { Logo } from "./Logo";
import { useBooks } from "../context/BooksContext";
import { SOCIAL_ICONS, SOCIAL_ORDER } from "../data/socialIcons";

const CONTACT_EMAIL = "info@digsbyearlylearning.com";
const API = process.env.REACT_APP_BACKEND_URL;

export default function Footer() {
  const year = new Date().getFullYear();
  const { books } = useBooks();
  const comingSoon = books.filter((b) => b.status === "coming_soon");
  const [socials, setSocials] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/api/social`)
      .then(({ data }) => setSocials(Array.isArray(data) ? data : []))
      .catch(() => setSocials([]));
  }, []);

  const ordered = SOCIAL_ORDER
    .map((key) => socials.find((s) => s.platform === key))
    .filter(Boolean);
  return (
    <footer className="bg-white border-t-2 border-black/10 mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo height={140} />
          <p className="mt-4 text-sm text-slate-600 max-w-xs">
            Fun, educational books for growing minds, created by a 30+ year early childhood education veteran.
          </p>
        </div>

        <div>
          <h4 className="font-heading font-bold text-lg mb-4">Explore</h4>
          <ul className="space-y-2 text-slate-700 font-medium">
            <li><Link to="/" data-testid="footer-home" className="hover:text-[#0000FF]">Home</Link></li>
            <li><Link to="/shop" data-testid="footer-shop" className="hover:text-[#0000FF]">Shop the Books</Link></li>
            <li><Link to="/about" data-testid="footer-about" className="hover:text-[#0000FF]">About the Author</Link></li>
            <li><Link to="/contact" data-testid="footer-contact" className="hover:text-[#0000FF]">Contact</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
            <Sparkles size={16} className="text-[#FF0000]" /> Coming Soon
          </h4>
          <ul className="space-y-2 text-slate-700 font-medium">
            {comingSoon.map((b) => (
              <li key={b.slug}>
                <Link
                  to={`/books/${b.slug}`}
                  data-testid={`footer-coming-soon-${b.slug}`}
                  className="hover:text-[#FF0000]"
                >
                  {b.shortTitle}
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/#learning-circle"
            data-testid="footer-join-waitlist"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#FF0000] text-white text-sm font-bold px-4 py-2 hover:bg-[#d60000] transition-colors"
          >
            Join the Learning Circle
          </Link>
        </div>


        <div>
          <h4 className="font-heading font-bold text-lg mb-4">Follow us</h4>
          <div className="flex items-center gap-3 flex-wrap" data-testid="footer-socials">
            {ordered.map((s) => {
              const brand = SOCIAL_ICONS[s.platform] || {};
              const Icon = brand.Icon;
              return (
                <a
                  key={s.platform}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${s.platform}`}
                  aria-label={s.name}
                  title={s.name}
                  className="w-11 h-11 flex items-center justify-center rounded-full bg-[#FFFBF2] border-2 border-black/10 text-slate-700 hover:text-white hover:-translate-y-0.5 transition-all duration-200"
                  style={{ "--brand": brand.color || "#0000FF" }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = brand.color || "#0000FF"; e.currentTarget.style.borderColor = brand.color || "#0000FF"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ""; e.currentTarget.style.borderColor = ""; }}
                >
                  {Icon && <Icon size={20} />}
                </a>
              );
            })}
          </div>
        </div>
      </div>

      <div className="border-t-2 border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-slate-500">
          <p>© {year} Digsby Early Learning. All rights reserved.</p>
          <p>Books sold and shipped by Amazon.</p>
        </div>
      </div>
    </footer>
  );
}
